import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw,
  Copy,
  ExternalLink,
  AlertCircle,
  Hash,
  Mail,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import TraceabilityTimeline from '@/pages/traceability/components/TraceabilityTimeline';
import {
  AddStageForm,
  StageEditDialog,
  AttachFileDialog,
} from '@/pages/traceability/components/TraceabilitySection';
import {
  getTraceabilityByLclRequest,
  notifyRequestCreated,
  addStage,
  updateStage,
  removeStage,
  attachFileToStage,
  detachFileFromStage,
} from '@/pages/traceability/Services/traceabilityService';

/**
 * Sección de Trazabilidad dentro del detalle de la solicitud LCL.
 *
 * Gemela de `pages/traceability/components/TraceabilitySection.jsx`
 * (aérea): misma UI, pero consulta por `lclRequestId`. Vive dentro
 * de `pages/lcl-requests/` para poder evolucionar sin tocar el aéreo.
 *
 * Layout de dos columnas:
 *  - Izquierda: formulario para agregar hitos (icono, título,
 *    descripción y la opción de avisarle al cliente).
 *  - Derecha: la línea de tiempo con los hitos ya cargados.
 *
 * El sistema solo siembra el primer hito ("Recepción de solicitud")
 * al crear la solicitud; el resto los arma el operador uno a uno
 * según lo que vaya pasando en la operación.
 */
export default function LclTraceabilitySection({ lclRequestId, clienteSnapshot }) {
  const toast = useToast();
  const [tr, setTr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [attachingStage, setAttachingStage] = useState(null);
  const [deletingStage, setDeletingStage] = useState(null);

  const load = useCallback(async () => {
    if (!lclRequestId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const r = await getTraceabilityByLclRequest(lclRequestId);
      if (r?.success && r?.data) {
        setTr(r.data);
      } else {
        setTr(null);
        setNotFound(true);
      }
    } catch (err) {
      toast.error('Error al cargar la trazabilidad', err?.message);
    } finally {
      setLoading(false);
    }
  }, [lclRequestId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  /** Manda el correo de "solicitud registrada" con el número de seguimiento. */
  const onNotifyClient = useCallback(async () => {
    if (!tr?._id) return;
    setNotifying(true);
    try {
      const r = await notifyRequestCreated(tr._id);
      if (r?.success) {
        toast.success('Cliente informado', r?.message);
      } else {
        toast.error('No se pudo enviar el correo', r?.message);
      }
    } catch (err) {
      toast.error('Error al enviar el correo', err?.message);
    } finally {
      setNotifying(false);
    }
  }, [tr?._id, toast]);

  const onUpdateStage = useCallback(
    async (stage, patch) => {
      if (!tr?._id) return;
      try {
        const r = await updateStage(tr._id, stage._id, patch);
        if (r?.success) {
          setTr(r.data);
          toast.success('Hito actualizado');
        } else {
          toast.error('No se pudo actualizar', r?.message);
        }
      } catch (err) {
        toast.error('Error al actualizar el hito', err?.message);
      }
    },
    [tr?._id, toast],
  );

  /**
   * Agrega un hito. Devuelve true solo si se guardó, para que el
   * formulario de la izquierda sepa si limpiarse.
   */
  const onAddStage = useCallback(
    async (dto) => {
      if (!tr?._id) return false;
      try {
        const r = await addStage(tr._id, dto);
        if (r?.success) {
          setTr(r.data);
          // El backend avisa en el mensaje si el correo salió o no.
          if (dto.notificar && r?.aviso && !r.aviso.enviado) {
            toast.error('Hito agregado, pero no se notificó', r?.message);
          } else {
            toast.success('Hito agregado', dto.notificar ? r?.message : undefined);
          }
          return true;
        }
        toast.error('No se pudo agregar el hito', r?.message);
        return false;
      } catch (err) {
        toast.error('Error al agregar el hito', err?.message);
        return false;
      }
    },
    [tr?._id, toast],
  );

  const onEditStage = useCallback((stage) => setEditingStage(stage), []);
  const onDeleteStage = useCallback((stage) => setDeletingStage(stage), []);

  const confirmDeleteStage = useCallback(async () => {
    if (!tr?._id || !deletingStage) return;
    try {
      const r = await removeStage(tr._id, deletingStage._id);
      if (r?.success) {
        setTr(r.data);
        toast.success('Hito eliminado');
      } else {
        toast.error('No se pudo eliminar', r?.message);
      }
    } catch (err) {
      toast.error('Error al eliminar el hito', err?.message);
    } finally {
      setDeletingStage(null);
    }
  }, [tr?._id, deletingStage, toast]);

  const onAttachFile = useCallback((stage) => setAttachingStage(stage), []);

  const onAttachSubmit = useCallback(
    async (fileMeta) => {
      if (!tr?._id || !attachingStage) return;
      try {
        const r = await attachFileToStage(tr._id, attachingStage._id, fileMeta);
        if (r?.success) {
          setTr(r.data);
          toast.success('Archivo adjuntado');
        } else {
          toast.error('No se pudo adjuntar', r?.message);
        }
      } catch (err) {
        toast.error('Error al adjuntar el archivo', err?.message);
      } finally {
        setAttachingStage(null);
      }
    },
    [tr?._id, attachingStage, toast],
  );

  const onDetachFile = useCallback(
    async (stage, fileKey) => {
      if (!tr?._id) return;
      try {
        const r = await detachFileFromStage(tr._id, stage._id, fileKey);
        if (r?.success) {
          setTr(r.data);
          toast.success('Archivo quitado');
        } else {
          toast.error('No se pudo quitar el archivo', r?.message);
        }
      } catch (err) {
        toast.error('Error al quitar el archivo', err?.message);
      }
    },
    [tr?._id, toast],
  );

  const copyLink = useCallback(() => {
    if (!tr?.numero) return;
    const url = `${window.location.origin}/t/${encodeURIComponent(tr.numero)}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado', url);
    } catch {
      toast.info(url);
    }
  }, [tr?.numero, toast]);

  const clienteSinCorreo = !tr?.clienteSnapshot?.correo && !clienteSnapshot?.correo;

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
          <Hash size={13} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Trazabilidad
        </span>
        <div className="flex-1 h-px bg-border" />
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          title="Recargar"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {!tr && !loading && notFound && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              Esta solicitud no tiene trazabilidad. Se genera automáticamente al
              crear la solicitud, siempre que el cliente tenga un correo
              registrado.
            </p>
          </CardContent>
        </Card>
      )}

      {tr && (
        <>
          {/* Cabecera: número, acceso del cliente y aviso por correo */}
          <Card>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                  Número
                </span>
                <code className="px-2 py-0.5 rounded bg-muted text-sm font-mono">
                  {tr.numero}
                </code>
                <span className="text-[11px] text-muted-foreground">
                  · NIT {tr.clienteSnapshot?.nit || '—'}
                </span>
                <a
                  href={`/t/${encodeURIComponent(tr.numero)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary text-xs inline-flex items-center gap-1 hover:underline"
                  title="Ver como cliente"
                >
                  <ExternalLink className="h-3 w-3" />
                  Vista cliente
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={copyLink}>
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copiar link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onNotifyClient}
                  disabled={notifying || clienteSinCorreo}
                  title={
                    clienteSinCorreo
                      ? 'El cliente no tiene correo registrado'
                      : 'Enviarle el número de seguimiento por correo'
                  }
                >
                  {notifying ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 mr-1" />
                  )}
                  Informar al cliente
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Dos columnas: formulario a la izquierda, línea de tiempo a la derecha */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(300px,360px)_1fr] gap-4 items-start">
            <AddStageForm
              onSubmit={onAddStage}
              clienteSinCorreo={clienteSinCorreo}
              siguienteOrden={(tr.hitos || []).length}
            />
            <div className="min-w-0">
              <TraceabilityTimeline
                hitos={tr.hitos}
                mode="operator"
                onEditStage={onEditStage}
                onDeleteStage={onDeleteStage}
                onAttachFile={onAttachFile}
                onDetachFile={onDetachFile}
              />
            </div>
          </div>
        </>
      )}

      {/* Dialog: editar hito */}
      {editingStage && (
        <StageEditDialog
          open={!!editingStage}
          onOpenChange={(o) => !o && setEditingStage(null)}
          initial={{
            nombre: editingStage.nombre,
            icono: editingStage.icono,
            descripcion: editingStage.descripcion,
          }}
          onSubmit={async (dto) => {
            await onUpdateStage(editingStage, dto);
            setEditingStage(null);
          }}
        />
      )}

      {/* Dialog: adjuntar archivo */}
      {attachingStage && (
        <AttachFileDialog
          stage={attachingStage}
          onClose={() => setAttachingStage(null)}
          onSubmit={onAttachSubmit}
        />
      )}

      {/* Confirm: eliminar / omitir hito */}
      <AlertDialog
        open={!!deletingStage}
        onOpenChange={(o) => !o && setDeletingStage(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este hito?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el hito <strong>{deletingStage?.nombre}</strong> de la
              línea de tiempo. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStage}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

